import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import { useTranslation } from 'react-i18next';
import { TargetForm } from '@admin/components/forms/targets/targets-form.tsx';
import type { LogicTypeTargetWorkspaceWriteModel } from '@r/api/generated/client.schemas.ts';
import type { TargetLogicType } from '@admin/types/workspace/logic.ts';
import { LOGIC_TRANSLATION_KEYS } from '@admin/types/workspace/logic.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';

type Target = LogicTypeTargetWorkspaceWriteModel & {
  logicTargetType?: TargetLogicType;
};

interface CreateTargetDialogProps {
  onTargetCreated: (target: Target) => void;
  fixedType?: TargetLogicType;
}

export const CreateTargetDialog: FC<CreateTargetDialogProps> = ({ onTargetCreated, fixedType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const logicTranslations = useMemo(() => {
    return {
      [LogicLogicReadModelType.TARGET_PERF]: t(
        LOGIC_TRANSLATION_KEYS[LogicLogicReadModelType.TARGET_PERF]
      ),
      [LogicLogicReadModelType.TARGET_POINT]: t(
        LOGIC_TRANSLATION_KEYS[LogicLogicReadModelType.TARGET_POINT]
      )
    };
  }, [t]);

  const handleSuccess = useCallback(
    (target: Target) => {
      onTargetCreated(target);
      setIsOpen(false);
    },
    [onTargetCreated]
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button rounded="full" className="mt-6">
          {t('form.target.add')}
        </Button>
      </DialogTrigger>

      <DialogContent className="p-10 !rounded-3xl text-foreground">
        <DialogHeader className="gap-3">
          <DialogTitle className="text-2xl">{t('form.target.add')}</DialogTitle>
          <DialogDescription>{t('target.create.dialog.description.1')}</DialogDescription>
          <DialogDescription>
            {t('target.create.dialog.description.2')}
            <span className="flex flex-col gap-y-1 pl-5 space-y-2 list-disc text-start">
              <span>• {t('target.create.dialog.description.3')}</span>
              <span>• {t('target.create.dialog.description.4')}</span>
            </span>
          </DialogDescription>
          <DialogDescription>{t('target.create.dialog.description.5')}</DialogDescription>
          <DialogDescription>{t('target.create.dialog.description.6')}</DialogDescription>
          <DialogDescription className="pb-3">
            {t('target.create.dialog.description.7')}
          </DialogDescription>
          {fixedType && (
            <DialogDescription className="text-sm text-muted-foreground">
              {t('form.target.type.fixed.message', {
                type: logicTranslations[fixedType]
              })}
            </DialogDescription>
          )}
        </DialogHeader>

        <TargetForm onSubmitTarget={handleSuccess} fixedType={fixedType} />
      </DialogContent>
    </Dialog>
  );
};
